import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { base_url } from "../../constants";
import "./appVariants.css";

/**
 * App Variants - the feature flags behind show_varients.php.
 *
 * Turning one off hides that whole section in the mobile app, so each
 * toggle is a live production switch. Changes are staged locally and only
 * sent when the admin presses Save.
 */

const ICONS = {
  videos: "bx bx-video",
  pdf: "bx bx-file",
  exam: "bx bx-task",
  questions: "bx bx-help-circle",
};

const AppVariants = () => {
  document.title = "App Variants | Medicotoon";
  const navigate = useNavigate();

  const localdata = localStorage.getItem("elmatary_admin");
  const adminData = localdata ? JSON.parse(localdata) : {};

  const [variants, setVariants] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);

  const request = useCallback(
    async (path, payload = {}) => {
      const res = await axios.post(
        `${base_url}/admin/varients/${path}`,
        JSON.stringify({
          ...payload,
          admin_id: adminData?.admin_id,
          access_token: adminData?.access_token,
        })
      );

      if (res?.message === "Session Expired") {
        localStorage.removeItem("elmatary_admin");
        navigate("/login", { replace: true });
        return null;
      }
      if (res?.status === "success") return res.message;
      throw new Error(typeof res?.message === "string" ? res.message : "An error occurred");
    },
    [adminData?.admin_id, adminData?.access_token, navigate]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await request("select_varients.php");
      if (rows === null) return;
      const list = Array.isArray(rows) ? rows : [];
      setVariants(list);
      setDraft(
        list.reduce(
          (acc, v) => ({ ...acc, [v.variant_key]: Number(v.variant_value) ? 1 : 0 }),
          {}
        )
      );
    } catch (err) {
      setLoadError(err.message || "Failed to load app variants");
      setVariants([]);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(
    () =>
      variants.some(
        (v) => (Number(v.variant_value) ? 1 : 0) !== (draft[v.variant_key] ?? 0)
      ),
    [variants, draft]
  );

  const changedKeys = useMemo(
    () =>
      variants
        .filter((v) => (Number(v.variant_value) ? 1 : 0) !== (draft[v.variant_key] ?? 0))
        .map((v) => v.label || v.variant_key),
    [variants, draft]
  );

  const toggle = (key) =>
    setDraft((d) => ({ ...d, [key]: d[key] ? 0 : 1 }));

  const reset = () =>
    setDraft(
      variants.reduce(
        (acc, v) => ({ ...acc, [v.variant_key]: Number(v.variant_value) ? 1 : 0 }),
        {}
      )
    );

  const save = async () => {
    setSaving(true);
    try {
      const result = await request("edit_varients.php", { variants: draft });
      if (result === null) return;
      toast.success("App variants updated");
      load();
    } catch (err) {
      toast.error(err.message || "Failed to save app variants");
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = Object.values(draft).filter(Boolean).length;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Settings" breadcrumbItem="App Variants" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="av-head">
                    <div>
                      <h4 className="mb-1">App Variants</h4>
                      <p className="text-muted mb-0">
                        These switches control which sections the mobile app
                        shows. They are read live from{" "}
                        <code>show_varients.php</code> every time the app
                        starts, so a change here reaches users without a new
                        release.
                      </p>
                    </div>
                    <div className="av-actions">
                      <Button color="light" onClick={load} disabled={loading || saving}>
                        <i className="bx bx-refresh me-1" />
                        Reload
                      </Button>
                      {dirty ? (
                        <Button color="light" onClick={reset} disabled={saving}>
                          Discard
                        </Button>
                      ) : null}
                      <Button
                        color="primary"
                        onClick={save}
                        disabled={!dirty || saving}
                      >
                        {saving ? <Spinner size="sm" className="me-1" /> : null}
                        Save changes
                      </Button>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <div className="text-muted mt-2">Loading app variants...</div>
                    </div>
                  ) : loadError ? (
                    <div className="text-center py-5">
                      <i className="bx bx-error-circle av-big-icon text-danger" />
                      <h5 className="mt-3 mb-1">Something went wrong</h5>
                      <p className="text-muted mb-3">{loadError}</p>
                      <Button color="primary" outline onClick={load}>
                        Try again
                      </Button>
                    </div>
                  ) : !variants.length ? (
                    <div className="text-center py-5">
                      <i className="bx bx-toggle-left av-big-icon" />
                      <h5 className="mt-3 mb-1">No variants found</h5>
                      <p className="text-muted mb-0">
                        Run the question bank migration to create the{" "}
                        <code>app_variants</code> table.
                      </p>
                    </div>
                  ) : (
                    <>
                      {dirty ? (
                        <div className="alert alert-warning mt-3 mb-0">
                          <i className="bx bx-error me-1" />
                          Unsaved changes to: <b>{changedKeys.join(", ")}</b>.
                          These affect the live app as soon as you save.
                        </div>
                      ) : null}

                      <div className="av-summary mt-3">
                        <span className="text-muted">
                          {enabledCount} of {variants.length} sections enabled
                        </span>
                      </div>

                      <Row className="g-3 mt-1">
                        {variants.map((variant) => {
                          const on = !!draft[variant.variant_key];
                          return (
                            <Col md={6} xl={3} key={variant.variant_key}>
                              <button
                                type="button"
                                className={`av-card ${on ? "is-on" : "is-off"}`}
                                onClick={() => toggle(variant.variant_key)}
                                disabled={saving}
                              >
                                <div className="av-card-top">
                                  <i
                                    className={`${
                                      ICONS[variant.variant_key] || "bx bx-toggle-left"
                                    } av-card-icon`}
                                  />
                                  <span
                                    className={`badge ${on ? "bg-success" : "bg-secondary"}`}
                                  >
                                    {on ? "Shown" : "Hidden"}
                                  </span>
                                </div>

                                <h5 className="mb-1">
                                  {variant.label || variant.variant_key}
                                </h5>
                                <p className="av-card-desc">
                                  {variant.description || "No description"}
                                </p>

                                <div className="av-card-foot">
                                  <code>{variant.variant_key}</code>
                                  <span className={`av-switch ${on ? "is-on" : ""}`}>
                                    <span className="av-switch-knob" />
                                  </span>
                                </div>
                              </button>
                            </Col>
                          );
                        })}
                      </Row>

                      <div className="av-preview mt-4">
                        <div className="text-muted small mb-1">
                          What <code>show_varients.php</code> will return after saving:
                        </div>
                        <pre className="av-code">
{JSON.stringify(
  variants.reduce(
    (acc, v) => ({ ...acc, [v.variant_key]: draft[v.variant_key] ?? 0 }),
    {}
  ),
  null,
  2
)}
                        </pre>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ToastContainer />
    </React.Fragment>
  );
};

export default AppVariants;
